// Shared types for the AI legal-risk scanner. Used by both contributor-side
// (typing-time advisory panel) and admin-side (review-reader audit panel),
// across app-ui and network-ui. Mirrors the Zod schema in
// api/src/mastra/workflows/review-legal-check-workflow.ts.

export const LEGAL_CHECK_CATEGORIES = [
  'mnpi',
  'trade_secret',
  'nda_confidential',
  'pii',
  'internal_company_strategy',
  'customer_confidential',
] as const;

export type LegalCheckCategory = (typeof LEGAL_CHECK_CATEGORIES)[number];

export type LegalCheckSeverity = 'warn' | 'caution';

// Used by the contributor-facing advisory panel (no persistence).
export interface LegalCheckFlag {
  category: LegalCheckCategory;
  excerpt: string;
  explanation: string;
  severity: LegalCheckSeverity;
}

// Persisted shape returned from GET /review/:id/legal-flags. Includes
// linkage back to the review + step + field so admins can match each
// observation to the column it came from. `reviewStep` is serialised by
// the API as the numeric ReviewStep enum value.
export interface PersistedLegalCheckFlag extends LegalCheckFlag {
  id: number;
  reviewId: number;
  reviewStep: number;
  fieldName: string;
  detectedAt: string;
}

// Training-aligned labels. Wording mirrors the rule names in the
// contributor-training curriculum so contributors see the same vocabulary
// in both surfaces.
export const LEGAL_CHECK_CATEGORY_LABELS: Record<LegalCheckCategory, string> = {
  customer_confidential: 'Confidential customer information',
  internal_company_strategy: 'Internal company strategy',
  mnpi: 'Material non-public information',
  nda_confidential: 'NDA-protected information',
  pii: 'Personal information',
  trade_secret: 'Trade secret',
};
